'use client';
import React, { Suspense, useEffect, useState } from 'react';
import { IRepository, IResourceVersion, IUser } from '@/types';

import Loader from './Loader';

import EmptyRepoCard from '@/components/Repository/EmptyRepoCard';
import ClientFolderContents from './ClientFolderContents';
import useWebsockets from '@/hooks/useWebsockets';
import toast from 'react-hot-toast';
import Spinner from '@/ui/Spinner';
import { colors } from '@/styles';

type WebSocketPayload = {
  status: 'success' | 'failure';
};

// import useOpenDataframes from '@/hooks/useRemoteDataframes';

const RepositoryFolderContents: React.FC<{
  repository: IRepository;
  resource: IResourceVersion;
  page?: number;
  limit?: number;
  currentUser?: IUser;
  authToken?: string;
  children?: React.ReactNode;
}> = ({ repository, resource, page, limit, currentUser, authToken, children }) => {
  const isEmptyRepository = repository.latest_commit === null;

  useWebsockets({
    room: `repository:${repository.id}`,
    token: authToken,
    events: ['import_status_changed'],
    onNewMessage(payload: WebSocketPayload) {
      toast.remove('importToast');

      switch (payload.status) {
        case 'success':
          toast.success('Done importing! Refresh this page to see the files.', { duration: Infinity });
          break;
        case 'failure':
          toast.error('The data import process failed.', { duration: Infinity });
          break;
      }
    },
  });

  useEffect(() => {
    if (['queued', 'running'].includes(repository.import_status ?? '')) {
      toast.custom(
        (t) => (
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              width: '350px',
              border: `1px solid ${colors.secondary.lightGray3}`,
              borderRadius: '5px',
              boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <div style={{ display: 'flex' }}>
              <Spinner />
              <span style={{ marginLeft: '8px' }}>Import in progress...</span>
            </div>

            <div style={{ marginTop: '10px' }}>We are processing your data. Come back later!</div>
          </div>
        ),
        { id: 'importToast', duration: Infinity },
      );
    }

    return () => {
      toast.remove('importToast');
    };
  }, []);

  // const { status, isLoading, isValidating, entries, error } = useOpenDataframes({
  //   repository,
  //   branch: resource.version,
  // });

  // console.log('status', status);
  // console.log('entries', entries);

  return (
    <>
      <Suspense fallback={<Loader rows={limit} />}>
        {isEmptyRepository ? (
          <EmptyRepoCard repository={repository} currentUser={currentUser} />
        ) : (
          <ClientFolderContents
            repository={repository}
            resource={resource}
            page={page}
            limit={limit}
            authToken={authToken}
          >
            {children}
          </ClientFolderContents>
        )}
      </Suspense>
    </>
  );
};

export default RepositoryFolderContents;
